import { getCexLabel } from "@cede/utils";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import {
  authorizeKatanaDelegation,
  formatKatanaSignTypedData,
  prepareKatanaDelegation,
} from "@/utils/katana/katana-auth";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

interface UseKatanaAuthProps {
  /**
   * Privy custody EVM address. Required to mint the delegated key — the
   * server-side `prepare` phase rejects the call without it. Hook callers
   * usually pass `hubUser?.defiAddress`; if it's `undefined`, the mutation
   * throws a tagged `KATANA_MISSING_DEFI_ADDRESS` error on submit.
   */
  defiAddress: string | undefined;
  /**
   * Invoked after Katana confirms the delegated-key registration and the
   * backend has stored the new exchange account. The caller is expected to
   * refetch the user's account surface and (if it wants to) hand the new
   * account off to its own `onSuccess` consumer — see `useHubUser` for the
   * canonical wiring.
   */
  onSuccess?: (accountId: number) => Promise<void> | void;
  onError?: (error: Error) => void;
}

/**
 * Drives the two-phase Katana delegated-key onboarding flow from the
 * browser:
 *   1. `POST /v1/exchange-account/katana/prepare` — server mints a fresh
 *      delegated keypair and returns the EIP-712 typed-data envelope the
 *      user must sign with their Privy custody wallet.
 *   2. Wagmi `signTypedDataAsync(...)` — the user's connected wallet
 *      produces the signature.
 *   3. `POST /v1/exchange-account/katana/authorize` — server verifies the
 *      signature, registers the delegated key with Katana, and persists
 *      the exchange account row.
 *
 * Surfaces a snackbar on success / error. Compose with `useHubUser` for the
 * full hub-user side effects (refetch, downstream success callback,
 * referral invalidation).
 */
export const useKatanaAuth = ({ defiAddress, onSuccess, onError }: UseKatanaAuthProps) => {
  const apiHelper = useApi();
  const { identityToken, getAccessToken, identityProvider, refreshIdentityToken } = useAuthenticationProvider();
  const { signTypedDataAsync } = useSignTypedDataWithConnection();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: connectKatanaAccount, isPending: connectKatanaAccountIsLoading } = useMutation({
    mutationFn: async (params: { apiKey: string; apiSecret: string }) => {
      if (!defiAddress) {
        throw Object.assign(
          new Error("Connect your Privy wallet before connecting Katana — no custody address found."),
          { type: "KATANA_MISSING_DEFI_ADDRESS" },
        );
      }
      const authCreds = { identityToken, getAccessToken, identityProvider, refreshIdentityToken };
      const prepareResult = await prepareKatanaDelegation(apiHelper, authCreds, {
        apiKey: params.apiKey,
        apiSecret: params.apiSecret,
        walletAddress: defiAddress,
      });
      const signature = await signTypedDataAsync(formatKatanaSignTypedData(prepareResult));
      const authorizeResult = await authorizeKatanaDelegation(apiHelper, authCreds, {
        prepareId: prepareResult.prepareId,
        signature,
      });

      await onSuccess?.(authorizeResult.accountId);
      enqueueSnackbar(`Exchange account for ${getCexLabel("katana")} was added successfully`, { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(error instanceof Error ? error.message : "Failed to connect Katana", { variant: "error" });
      onError?.(error as Error);
    },
  });

  return { connectKatanaAccount, connectKatanaAccountIsLoading };
};
